import { z } from "zod"

import { isSafeObjectKey } from "./connector-tool-policy-path"

export const MAX_JSON_VALUE_BYTES = 4_000
export const MAX_JSON_DEPTH = 4
export const MAX_JSON_ENTRIES = 50
export const MAX_JSON_REF_TOKENS = 32
export const MAX_JSON_LEAF_LENGTH = 1_000
export const MAX_JSON_KEY_LENGTH = 128

// A Record here makes TemplatedJson circularly reference itself (TS2456).
export interface TemplatedJsonObject {
	[key: string]: TemplatedJson
}

/** A JSON value a policy can pin, whose string leaves may carry `{{ref}}` tokens. */
export type TemplatedJson =
	| string
	| number
	| boolean
	| null
	| TemplatedJson[]
	| TemplatedJsonObject

// Braces cannot appear in a body, so counted quantifiers like {2,4} can never
// reach a pattern and nesting is impossible by construction.
export const TEMPLATE_TOKEN_SOURCE = String.raw`\{\{\s*([^{}]+?)\s*\}\}`

/** A fresh instance per call: a shared /g regex carries lastIndex state. */
export function templateTokenRegex(): RegExp {
	return new RegExp(TEMPLATE_TOKEN_SOURCE, "g")
}

export function templateTokenBodies(text: string): string[] {
	return [...text.matchAll(templateTokenRegex())].map((match) => match[1]!)
}

export interface TemplateTokenSpan {
	body: string
	start: number
	end: number
}

/** Every token with where it sits, for rules that depend on what surrounds it. */
export function templateTokenSpans(text: string): TemplateTokenSpan[] {
	return [...text.matchAll(templateTokenRegex())].map((match) => ({
		body: match[1]!,
		start: match.index,
		end: match.index + match[0].length,
	}))
}

export function isJsonContainer(
	value: TemplatedJson,
): value is TemplatedJson[] | TemplatedJsonObject {
	return typeof value === "object" && value !== null
}

export function visitJsonStrings(
	value: TemplatedJson,
	visit: (text: string) => void,
): void {
	if (typeof value === "string") return visit(value)
	if (Array.isArray(value)) {
		for (const entry of value) visitJsonStrings(entry, visit)
		return
	}
	if (isJsonContainer(value)) {
		for (const entry of Object.values(value)) visitJsonStrings(entry, visit)
	}
}

/** Every distinct token body in the value's string leaves, in first-seen order. */
export function tokensInTemplatedJson(value: TemplatedJson): string[] {
	const tokens: string[] = []
	visitJsonStrings(value, (text) => {
		for (const body of templateTokenBodies(text)) {
			if (!tokens.includes(body)) tokens.push(body)
		}
	})
	return tokens
}

export function mapJsonStrings(
	value: TemplatedJson,
	map: (text: string) => TemplatedJson,
): TemplatedJson {
	if (typeof value === "string") return map(value)
	if (Array.isArray(value)) {
		return value.map((entry) => mapJsonStrings(entry, map))
	}
	if (isJsonContainer(value)) {
		const mapped: TemplatedJsonObject = {}
		for (const [key, entry] of Object.entries(value)) {
			mapped[key] = mapJsonStrings(entry, map)
		}
		return mapped
	}
	return value
}

function withinEntryCap(value: TemplatedJson): boolean {
	if (!isJsonContainer(value)) return true
	const entries = Array.isArray(value) ? value : Object.values(value)
	if (entries.length > MAX_JSON_ENTRIES) return false
	return entries.every(withinEntryCap)
}

function jsonDepth(value: TemplatedJson): number {
	if (!isJsonContainer(value)) return 0
	const entries = Array.isArray(value) ? value : Object.values(value)
	return (
		1 +
		entries.reduce<number>((deepest, entry) => {
			const depth = jsonDepth(entry)
			return depth > deepest ? depth : deepest
		}, 0)
	)
}

function hasSafeKeys(value: TemplatedJson): boolean {
	if (!isJsonContainer(value)) return true
	if (Array.isArray(value)) return value.every(hasSafeKeys)
	return Object.entries(value).every(
		([key, entry]) => isSafeObjectKey(key) && hasSafeKeys(entry),
	)
}

/**
 * The same caps the stored schema enforces, for a value that arrived already
 * resolved and so cannot be checked with the token-validating schema.
 */
export function isWithinJsonLimits(value: TemplatedJson): boolean {
	if (!isJsonContainer(value)) return false
	const encoded = JSON.stringify(value)
	if (encoded === undefined || encoded.length > MAX_JSON_VALUE_BYTES)
		return false
	return (
		jsonDepth(value) <= MAX_JSON_DEPTH &&
		withinEntryCap(value) &&
		hasSafeKeys(value)
	)
}

const ObjectKey = z
	.string()
	.min(1)
	.max(MAX_JSON_KEY_LENGTH)
	.refine(isSafeObjectKey, { message: "is not a key a policy may write" })

export interface TemplatedJsonOptions {
	/** Rejects a leaf whose `{{token}}` names nothing we could resolve. */
	isValidLeaf?: (text: string) => boolean
}

/**
 * Depth is bounded by construction rather than by a recursion guard, so an
 * over-deep value is rejected by the type instead of at walk time.
 */
export function templatedJsonValue(options: TemplatedJsonOptions = {}) {
	const isValidLeaf = options.isValidLeaf ?? (() => true)

	const leaf = z.union([
		z.string().max(MAX_JSON_LEAF_LENGTH).refine(isValidLeaf, {
			message: "contains a token that is not a valid reference",
		}),
		z.number().finite(),
		z.boolean(),
		z.null(),
	])

	// The array branch precedes the record branch so an array never falls into it.
	const atDepth = (depth: number): z.ZodType<TemplatedJson> => {
		if (depth === 0) return leaf as z.ZodType<TemplatedJson>
		const inner = atDepth(depth - 1)
		return z.union([
			leaf,
			z.array(inner).max(MAX_JSON_ENTRIES),
			z.record(ObjectKey, inner),
		]) as z.ZodType<TemplatedJson>
	}

	return atDepth(MAX_JSON_DEPTH).superRefine((value, ctx) => {
		if (!isJsonContainer(value)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "must be an object or a list",
			})
			return
		}
		const encoded = JSON.stringify(value)
		if (encoded === undefined || encoded.length > MAX_JSON_VALUE_BYTES) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: `at most ${MAX_JSON_VALUE_BYTES} bytes`,
			})
		}
		if (!withinEntryCap(value)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: `at most ${MAX_JSON_ENTRIES} entries per object or list`,
			})
		}
		if (tokensInTemplatedJson(value).length > MAX_JSON_REF_TOKENS) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: `at most ${MAX_JSON_REF_TOKENS} references`,
			})
		}
	})
}
